import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { LogIn, Mail, Clock, Heart, MessageSquare, Send, LayoutDashboard } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import Editor from '@/components/Editor';
import DeliveryForm from '@/components/DeliveryForm';
import PricingCard from '@/components/PricingCard';

export default function Index() {
  const [letterContent, setLetterContent] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [user, setUser] = useState<User | null>(null);
  const [errors, setErrors] = useState({
    content: false,
    deliveryDate: false,
    email: false,
    whatsapp: false,
  });

  const editorSectionRef = useRef<HTMLDivElement>(null);
  const formSectionRef = useRef<HTMLDivElement>(null);

  const handleSetErrors: typeof setErrors = (update) => {
    setErrors((prev) => {
      const next = typeof update === 'function' ? update(prev) : update;

      if (next.content) {
        editorSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (next.deliveryDate || next.email || next.whatsapp) {
        formSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return next;
    });
  };

  useEffect(() => {
    // Verificar se o usuário está logado
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user || null);
    };

    checkAuth();

    // Escutar mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user || null);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md sticky top-0 z-40 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="h-10 w-10">
                <img 
                  src="/lovable-uploads/8fe05359-d2c7-4418-abd6-8deff9f146be.png" 
                  alt="Logo Carta para o Futuro - Escrever carta para o futuro" 
                  className="h-full w-full object-contain"
                />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
                Carta para o Futuro
              </span>
            </div>
            {user ? (
              <Link
                to="/admin"
                className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                aria-label="Acessar painel administrativo"
              >
                <LayoutDashboard className="h-5 w-5" />
                <span>Painel</span>
              </Link>
            ) : (
              <Link
                to="/entrar"
                className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                aria-label="Fazer login na plataforma de cartas para o futuro"
              >
                <LogIn className="h-5 w-5" />
                <span>Entrar</span>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Hero Section */}
          <div className="text-center mb-16 relative">
            <div className="absolute inset-0 -z-10">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(59,130,246,0.08)_0%,rgba(59,130,246,0)_100%)]" />
            </div>
            <div className="inline-flex items-center justify-center px-4 py-1.5 mb-6 text-sm font-medium rounded-full bg-blue-50 text-blue-600 border border-blue-100">
              <Mail className="w-4 h-4 mr-2" />
              Escreva hoje, surpreenda-se amanhã
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6 bg-gradient-to-r from-gray-900 to-gray-600 text-transparent bg-clip-text">
              Escreva uma <span className="text-blue-600">Carta para seu Futuro</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              Capture seus pensamentos, sonhos e aspirações em uma carta que será entregue por <strong>email ou WhatsApp</strong> na data que você escolher. Uma cápsula do tempo digital para seu eu do futuro.
            </p>
            
            {/* SEO Benefits Section */}
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12 text-left">
              <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-gray-100">
                <div className="flex items-center mb-3">
                  <Clock className="h-6 w-6 text-blue-600 mr-2" />
                  <h3 className="font-semibold text-gray-900">Entrega Agendada</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Escolha a data exata para receber sua carta do passado. Pode ser em 1 mês, 1 ano ou 10 anos.
                </p>
              </div>
              
              <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-gray-100">
                <div className="flex items-center mb-3">
                  <MessageSquare className="h-6 w-6 text-blue-600 mr-2" />
                  <h3 className="font-semibold text-gray-900">Email + WhatsApp</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Receba sua carta por email e WhatsApp para garantir que você não perca essa mensagem especial.
                </p>
              </div>
              
              <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-gray-100">
                <div className="flex items-center mb-3">
                  <Heart className="h-6 w-6 text-blue-600 mr-2" />
                  <h3 className="font-semibold text-gray-900">Momento Especial</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Crie um momento único de reflexão e conexão com seu eu do passado através de suas próprias palavras.
                </p>
              </div>
            </div>
          </div>

          {/* SEO-optimized content section */}
          <div className="max-w-4xl mx-auto mb-16">
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 border border-gray-100">
              <h2 className="text-3xl font-bold text-center mb-6 text-gray-900">
                Como Funciona a Carta para o Futuro?
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-blue-600">1</span>
                  </div>
                  <h3 className="font-semibold mb-2">Escreva sua Carta</h3>
                  <p className="text-gray-600 text-sm">
                    Digite seus pensamentos, sonhos, metas e mensagens para seu eu do futuro
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-blue-600">2</span>
                  </div>
                  <h3 className="font-semibold mb-2">Escolha a Data</h3>
                  <p className="text-gray-600 text-sm">
                    Selecione quando quer receber sua carta: 6 meses, 1 ano ou mais
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <Send className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="font-semibold mb-2">Receba no Futuro</h3>
                  <p className="text-gray-600 text-sm">
                    Sua carta será entregue por email e WhatsApp na data escolhida
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Editor and Forms */}
          <div className="max-w-4xl mx-auto space-y-16">
            <section aria-label="Editor de carta para o futuro" ref={editorSectionRef}>
              <Editor
                onContentChange={(content) => {
                  setLetterContent(content);
                  if (errors.content && content.length >= 10) {
                    setErrors((prev) => ({ ...prev, content: false }));
                  }
                }}
                hasError={errors.content}
              />
            </section>
            
            <section aria-label="Formulário de entrega da carta" ref={formSectionRef}>
              <DeliveryForm
                onDeliveryDateChange={(date) => {
                  setDeliveryDate(date);
                  if (errors.deliveryDate && date) {
                    setErrors((prev) => ({ ...prev, deliveryDate: false }));
                  }
                }}
                onEmailChange={(value) => {
                  setEmail(value);
                  if (errors.email && value) {
                    setErrors((prev) => ({ ...prev, email: false }));
                  }
                }}
                onWhatsAppChange={(value) => {
                  setWhatsapp(value);
                  if (errors.whatsapp && value) {
                    setErrors((prev) => ({ ...prev, whatsapp: false }));
                  }
                }}
                deliveryDate={deliveryDate}
                email={email}
                whatsapp={whatsapp}
                deliveryDateError={errors.deliveryDate}
                emailError={errors.email}
                whatsappError={errors.whatsapp}
              />
            </section>
            
            <section aria-label="Finalizar pedido da carta para o futuro">
              <PricingCard
                content={letterContent}
                deliveryDate={deliveryDate}
                email={email}
                whatsapp={whatsapp}
                setErrors={handleSetErrors}
              />
            </section>
          </div>

          {/* FAQ Section for SEO */}
          <div className="max-w-4xl mx-auto mt-20">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
              Perguntas Frequentes sobre Carta para o Futuro
            </h2>
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 border border-gray-100">
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-gray-900">O que é uma carta para o futuro?</h3>
                  <p className="text-gray-600">
                    Uma carta para o futuro é uma mensagem que você escreve hoje para receber em uma data futura. É como uma cápsula do tempo digital onde você pode capturar seus pensamentos, sonhos e metas atuais para reencontrar no futuro.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-gray-900">Como posso ter certeza de que vou receber minha carta?</h3>
                  <p className="text-gray-600">
                    Sua carta será enviada tanto por email quanto por WhatsApp na data escolhida, garantindo dupla entrega. Nosso sistema é automatizado e confiável.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-gray-900">Posso escrever quantas cartas quiser?</h3>
                  <p className="text-gray-600">
                    Sim! Você pode criar várias cartas para diferentes datas futuras. A carta avulsa custa R$ 19,90. O plano Premium custa R$ 29,90 por ano.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-gray-900">Minha carta fica segura?</h3>
                  <p className="text-gray-600">
                    Absolutamente. Todas as cartas são armazenadas de forma segura e criptografada até a data de entrega.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white/50 backdrop-blur-sm mt-24 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Carta para o Futuro</h2>
              <p className="text-gray-600 text-sm max-w-md mx-auto">
                A plataforma mais confiável para escrever cartas para o futuro no Brasil. 
                Escreva hoje, surpreenda-se amanhã.
              </p>
            </div>
            <div className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Cartas Pro Futuro. Todos os direitos reservados. 
              Desenvolvido por Max Roque.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
